import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Shield,
  ShieldOff,
  Save,
  Activity,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Search,
  Sparkles,
} from 'lucide-react';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { VerdictBadge } from './AdminBotCloaking';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'hitzlink.com';

type Range = 'today' | 'yesterday' | '7d' | '30d';
type Verdict = 'off' | 'idle' | 'normal' | 'elevated' | 'aggressive' | 'lenient';

interface DetailResponse {
  page: {
    id: string;
    name: string;
    slug: string | null;
    createdAt: string;
    updatedAt: string;
    owner: { id: string; name: string | null; email: string | null; subscription: string };
  };
  cloaking: {
    enabled: boolean;
    allowedCountries: string[];
    blockedCountries: string[];
    allowedCities: string[];
    blockedAsns: Array<number | string>;
    autoBlockDatacenters: boolean;
    adNiche: string | null;
    safePageTemplate: string | null;
    safePageUrl: string;
    aiVariationIndex: number | null;
  };
  range: Range;
  startDate: string;
  endDate: string;
  summary: {
    totalBlocks: number;
    views: number;
    totalHits: number;
    blockRate: number;
    falsePositiveCount: number;
    falsePositiveRate: number;
  };
  breakdown: {
    byReason: Record<string, number>;
    byCountry: Record<string, number>;
    byBotType: Record<string, number>;
  };
  verdict: { status: Verdict; severity: number; notes: string[] };
  falsePositives: Array<{
    id: string;
    uaCategory: string;
    reason: string;
    country: string;
    ip: string;
    userAgent: string;
    createdAt: string;
  }>;
  recommendations?: Array<{
    id: string;
    scope: 'global' | 'page';
    key: string | null;
    direction: 'increase' | 'decrease' | 'enable' | 'disable' | 'review';
    currentValue: unknown;
    suggestedValue: unknown;
    severity: 'critical' | 'high' | 'medium' | 'low';
    confidence: 'high' | 'medium' | 'low';
    evidenceCount: number;
    evidencePct: number;
    rationaleKey: string;
    rationaleVars: Record<string, unknown>;
  }>;
  logs: Array<{
    id: string;
    botType: string;
    botName: string | null;
    reason: string;
    pattern: string | null;
    ip: string;
    userAgent: string;
    deviceSummary: string;
    country: string | null;
    safePageUrl: string | null;
    createdAt: string;
  }>;
}

const VERDICT_NOTE_LABELS: Record<string, string> = {
  'cloaking-disabled': 'Cloaking is currently OFF. Bot traffic is not being filtered.',
  'no-blocks-in-window': 'No blocks recorded in this window — cloaking is idle.',
  'block-rate-high': 'Over 50% of all hits are being blocked. Real users are likely affected.',
  'block-rate-elevated': 'Block rate above 30% — worth a closer look.',
  'false-positives-high':
    'Over 20% of allowed-country blocks match a trusted real-user UA (FB/IG in-app, Apple Private Relay).',
  'block-rate-very-low':
    'Block rate is unusually low. Cloaking may not be matching the threats it is supposed to catch.',
  'has-false-positives':
    'Some blocks look like real humans (FB/IG in-app browser or Apple Private Relay) inside an allowed country.',
  'behavior-score-blocks-with-allowlist':
    'Visitors are being blocked by behaviour-score with a country allow-list active. Consider lowering CLOAK_BEHAVIOR_THRESHOLD if real users get through pre-cloak.',
};

const RANGE_LABELS: Record<Range, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
};

// Mirror of the safePageTemplate IDs in server/safePages/templates.js — kept
// in this file so the admin can pin a deterministic safe page without us
// having to ship a 1300-line templates module to the client. Update both
// places together when adding new templates.
const SAFE_PAGE_TEMPLATE_OPTIONS = [
  { id: '', label: 'Auto (deterministic across full pool)' },
  { id: 'category:recipe', label: 'Category · Recipe' },
  { id: 'category:travel', label: 'Category · Travel' },
  { id: 'category:health', label: 'Category · Health' },
  { id: 'category:tech', label: 'Category · Tech' },
  { id: 'category:finance', label: 'Category · Finance' },
  { id: 'category:diy', label: 'Category · DIY' },
  { id: 'category:books', label: 'Category · Books' },
  { id: 'category:news', label: 'Category · News' },
  { id: 'category:productivity', label: 'Category · Productivity' },
  { id: 'category:sports', label: 'Category · Sports' },
  { id: 'category:lifestyle', label: 'Category · Lifestyle' },
];

const AD_NICHE_OPTIONS = [
  { id: '', label: 'No niche hint' },
  { id: 'general', label: 'General' },
  { id: 'gambling', label: 'Sports / probability / strategy' },
  { id: 'finance', label: 'Finance' },
  { id: 'loan', label: 'Loan' },
  { id: 'crypto', label: 'Crypto' },
  { id: 'health', label: 'Health' },
  { id: 'tech', label: 'Tech' },
];

export const AdminPageCloaking: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const [range, setRange] = useState<Range>('today');
  const [data, setData] = useState<DetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Editor draft — locally mutable, committed via PATCH on save.
  const [draft, setDraft] = useState<DetailResponse['cloaking'] | null>(null);
  const [saving, setSaving] = useState(false);

  const [logSearch, setLogSearch] = useState('');
  const [logReason, setLogReason] = useState<string>('');

  const fetchDetail = async (showSpinner = true) => {
    if (!token || !pageId) return;
    if (showSpinner) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await fetch(
        `${API_URL}/admin/pages/${pageId}/cloaking?range=${range}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: DetailResponse = await res.json();
      setData(json);
      setDraft(json.cloaking);
    } catch (err) {
      console.error('[AdminPageCloaking] fetch error:', err);
      toast.error('Failed to load page cloaking');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDetail(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, range]);

  const dirty = useMemo(() => {
    if (!data || !draft) return false;
    return JSON.stringify(data.cloaking) !== JSON.stringify(draft);
  }, [data, draft]);

  const handleSave = async () => {
    if (!draft || !pageId || !token) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/pages/${pageId}/cloaking`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(draft),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt}`);
      }
      toast.success('Cloaking settings saved');
      await fetchDetail(false);
    } catch (err: any) {
      console.error('[AdminPageCloaking] save error:', err);
      toast.error(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const filteredLogs = useMemo(() => {
    if (!data) return [];
    const q = logSearch.trim().toLowerCase();
    return data.logs.filter((l) => {
      if (logReason && l.reason !== logReason) return false;
      if (!q) return true;
      return (
        (l.ip || '').toLowerCase().includes(q) ||
        (l.country || '').toLowerCase().includes(q) ||
        (l.userAgent || '').toLowerCase().includes(q) ||
        (l.deviceSummary || '').toLowerCase().includes(q) ||
        (l.botName || '').toLowerCase().includes(q) ||
        (l.botType || '').toLowerCase().includes(q)
      );
    });
  }, [data, logSearch, logReason]);

  const reasonOptions = useMemo(() => {
    if (!data) return [];
    return Object.keys(data.breakdown.byReason).sort();
  }, [data]);

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <Activity className="w-12 h-12 text-purple-600 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  if (!data || !draft) {
    return (
      <AdminLayout>
        <div className="p-4 sm:p-6 lg:p-8">
          <button
            onClick={() => navigate('/admin/cloaking')}
            className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <p className="text-gray-700">Page not found.</p>
        </div>
      </AdminLayout>
    );
  }

  const pageUrl = data.page.slug
    ? `https://${data.page.slug}.${BASE_DOMAIN}`
    : `https://${BASE_DOMAIN}/${data.page.id}`;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
        {/* Header */}
        <button
          onClick={() => navigate('/admin/cloaking')}
          className="text-sm text-gray-600 hover:text-gray-900 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back to overview
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{data.page.name}</h1>
              <VerdictBadge verdict={data.verdict.status} severity={data.verdict.severity} />
            </div>
            <div className="text-sm text-gray-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
              <span>
                {data.page.owner.name || '—'}{' '}
                <span className="text-gray-400">({data.page.owner.email || '—'})</span>
              </span>
              <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs font-medium uppercase">
                {data.page.owner.subscription}
              </span>
              <a
                href={pageUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:underline"
              >
                {pageUrl} <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <RangePicker value={range} onChange={setRange} />
            <button
              type="button"
              onClick={() => fetchDetail(false)}
              disabled={refreshing}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 text-sm font-medium text-gray-700 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tuning recommendations — surfaced ABOVE the legacy diagnostic
            notes because they're the actionable next-step the admin
            most likely came here for. Notes still live below for the
            "why is it red?" sanity check. */}
        {data.recommendations && data.recommendations.length > 0 && (
          <RecommendationsPanel recommendations={data.recommendations} />
        )}

        {/* Verdict notes */}
        {data.verdict.notes.length > 0 && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Diagnostic notes
            </p>
            <ul className="text-sm text-amber-900 space-y-1 list-disc pl-5">
              {data.verdict.notes.map((n) => (
                <li key={n}>{VERDICT_NOTE_LABELS[n] || n}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            label={`Blocks · ${RANGE_LABELS[range]}`}
            value={data.summary.totalBlocks.toLocaleString()}
            tone="orange"
          />
          <SummaryCard
            label="Views"
            value={data.summary.views.toLocaleString()}
            tone="emerald"
          />
          <SummaryCard
            label="Block rate"
            value={`${(data.summary.blockRate * 100).toFixed(1)}%`}
            tone={
              data.summary.blockRate >= 0.5
                ? 'rose'
                : data.summary.blockRate >= 0.3
                ? 'orange'
                : 'emerald'
            }
          />
          <SummaryCard
            label="False positives"
            value={data.summary.falsePositiveCount.toLocaleString()}
            sub={`${(data.summary.falsePositiveRate * 100).toFixed(1)}% of blocks`}
            tone={data.summary.falsePositiveCount > 0 ? 'amber' : 'emerald'}
          />
        </div>

        {/* Two-column layout: settings + breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Settings editor */}
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Cloaking settings</h2>
                <p className="text-sm text-gray-500">
                  Edits here override the user's saved config and bypass plan-gating.
                </p>
              </div>
              <button
                type="button"
                disabled={!dirty || saving}
                onClick={handleSave}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>

            <div className="space-y-5">
              <Toggle
                label="Cloaking enabled"
                description="Master switch. When off, every visitor sees the real landing page."
                value={draft.enabled}
                onChange={(v) => setDraft({ ...draft, enabled: v })}
              />

              <Toggle
                label="Auto-block datacenter ASNs"
                description="Recommended ON. Blocks Cloudflare/AWS/Google/etc. headless traffic."
                value={draft.autoBlockDatacenters}
                onChange={(v) => setDraft({ ...draft, autoBlockDatacenters: v })}
              />

              <CountriesField
                label="Allowed countries (ISO-2, comma-separated)"
                description="Empty = no geo filter. Visitors from any other country are sent to the safe page."
                value={draft.allowedCountries || []}
                onChange={(arr) => setDraft({ ...draft, allowedCountries: arr })}
              />

              <CountriesField
                label="Blocked countries (ISO-2, comma-separated)"
                description="Visitors from any of these are always sent to the safe page."
                value={draft.blockedCountries || []}
                onChange={(arr) => setDraft({ ...draft, blockedCountries: arr })}
              />

              <div>
                <label className="block text-sm font-medium text-gray-800 mb-1">Safe page URL</label>
                <p className="text-xs text-gray-500 mb-1.5">
                  Optional. If empty, the inline templated safe page is served.
                </p>
                <input
                  type="url"
                  value={draft.safePageUrl}
                  onChange={(e) => setDraft({ ...draft, safePageUrl: e.target.value })}
                  placeholder="https://example.com/blog"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-1">
                    Inline template
                  </label>
                  <select
                    value={draft.safePageTemplate || ''}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        safePageTemplate: e.target.value || null,
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {SAFE_PAGE_TEMPLATE_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-800 mb-1">
                    Ad niche hint
                  </label>
                  <select
                    value={draft.adNiche || ''}
                    onChange={(e) =>
                      setDraft({ ...draft, adNiche: e.target.value || null })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {AD_NICHE_OPTIONS.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
            <h2 className="text-lg font-semibold text-gray-900">Breakdown</h2>
            <BreakdownList title="By reason" data={data.breakdown.byReason} accent="orange" />
            <BreakdownList title="By country" data={data.breakdown.byCountry} accent="emerald" />
            <BreakdownList title="By bot type" data={data.breakdown.byBotType} accent="slate" />
          </div>
        </div>

        {/* False positives */}
        {data.falsePositives.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <h2 className="text-lg font-semibold text-amber-900 mb-1 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" /> Likely false positives ({data.falsePositives.length})
            </h2>
            <p className="text-sm text-amber-900 mb-4">
              These blocks come from an allowed country with a User-Agent that matches our
              trusted real-user allowlist (Facebook/Instagram in-app browser or Apple Private
              Relay). They are almost certainly real humans.
            </p>
            <div className="overflow-x-auto bg-white rounded-lg border border-amber-200">
              <table className="min-w-full text-sm">
                <thead className="bg-amber-100/60">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-amber-900">UA category</th>
                    <th className="px-3 py-2 text-left font-semibold text-amber-900">Reason</th>
                    <th className="px-3 py-2 text-left font-semibold text-amber-900">Country</th>
                    <th className="px-3 py-2 text-left font-semibold text-amber-900">IP</th>
                    <th className="px-3 py-2 text-left font-semibold text-amber-900">User-Agent</th>
                    <th className="px-3 py-2 text-left font-semibold text-amber-900">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amber-100">
                  {data.falsePositives.map((fp) => (
                    <tr key={fp.id}>
                      <td className="px-3 py-2 font-medium text-amber-900">{fp.uaCategory}</td>
                      <td className="px-3 py-2 text-gray-700">{fp.reason}</td>
                      <td className="px-3 py-2 text-gray-700">{fp.country || '—'}</td>
                      <td className="px-3 py-2 text-gray-700 font-mono text-xs">{fp.ip}</td>
                      <td className="px-3 py-2 text-gray-600 font-mono text-xs max-w-[420px] truncate" title={fp.userAgent}>
                        {fp.userAgent}
                      </td>
                      <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                        {new Date(fp.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bot logs */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Bot logs · {data.logs.length} sampled (latest 200 + 50/reason)
            </h2>
            <div className="flex items-center gap-2">
              <select
                value={logReason}
                onChange={(e) => setLogReason(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value="">All reasons</option>
                {reasonOptions.map((r) => (
                  <option key={r} value={r}>
                    {r} ({data.breakdown.byReason[r]})
                  </option>
                ))}
              </select>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="IP, country, device, UA…"
                  value={logSearch}
                  onChange={(e) => setLogSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 border-y border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Time</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Reason</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Bot name</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Country</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Device</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">IP</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">User-Agent</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLogs.map((l) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                      {new Date(l.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-block px-2 py-0.5 rounded bg-orange-50 text-orange-700 text-xs font-medium">
                        {l.reason}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{l.botType}</td>
                    <td className="px-3 py-2 text-gray-700">{l.botName || '—'}</td>
                    <td className="px-3 py-2 text-gray-700">{l.country || '—'}</td>
                    <td
                      className="px-3 py-2 text-gray-700 max-w-[280px] text-sm"
                      title={l.deviceSummary || ''}
                    >
                      {l.deviceSummary || '—'}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-600">{l.ip}</td>
                    <td
                      className="px-3 py-2 font-mono text-xs text-gray-500 max-w-[380px] truncate"
                      title={l.userAgent}
                    >
                      {l.userAgent}
                    </td>
                  </tr>
                ))}
                {filteredLogs.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-3 py-8 text-center text-gray-500">
                      No logs match the filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

// ---------------- subcomponents ----------------

function SummaryCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone: 'emerald' | 'orange' | 'rose' | 'amber';
}) {
  const toneCls: Record<typeof tone, string> = {
    emerald: 'bg-emerald-50 border-emerald-200',
    orange: 'bg-orange-50 border-orange-200',
    rose: 'bg-rose-50 border-rose-200',
    amber: 'bg-amber-50 border-amber-200',
  };
  return (
    <div className={`border rounded-xl p-5 ${toneCls[tone]}`}>
      <p className="text-xs uppercase tracking-wide text-gray-600 font-medium">{label}</p>
      <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function Toggle({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-gray-800 flex items-center gap-2">
          {value ? (
            <Shield className="w-4 h-4 text-emerald-600" />
          ) : (
            <ShieldOff className="w-4 h-4 text-gray-400" />
          )}
          {label}
        </p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
          value ? 'bg-emerald-500' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 rounded-full bg-white transform transition-transform mt-0.5 ${
            value ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  );
}

function CountriesField({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: string[];
  onChange: (arr: string[]) => void;
}) {
  const [text, setText] = useState(value.join(', '));
  // Re-sync when external value changes (e.g. after refetch).
  useEffect(() => {
    setText(value.join(', '));
  }, [value]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-800 mb-1">{label}</label>
      {description && <p className="text-xs text-gray-500 mb-1.5">{description}</p>}
      <input
        type="text"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const arr = e.target.value
            .split(/[\s,]+/)
            .map((c) => c.trim().toUpperCase())
            .filter(Boolean);
          onChange(arr);
        }}
        placeholder="TH, US, GB"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
    </div>
  );
}

function BreakdownList({
  title,
  data,
  accent,
}: {
  title: string;
  data: Record<string, number>;
  accent: 'orange' | 'emerald' | 'slate';
}) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  const accentCls: Record<typeof accent, string> = {
    orange: 'bg-orange-500',
    emerald: 'bg-emerald-500',
    slate: 'bg-slate-500',
  };
  return (
    <div>
      <p className="text-sm font-semibold text-gray-700 mb-2">{title}</p>
      {entries.length === 0 ? (
        <p className="text-xs text-gray-400">No data.</p>
      ) : (
        <ul className="space-y-1.5">
          {entries.map(([k, v]) => {
            const pct = total > 0 ? (v / total) * 100 : 0;
            return (
              <li key={k}>
                <div className="flex items-center justify-between text-xs text-gray-600 mb-0.5">
                  <span className="truncate">{k}</span>
                  <span className="font-medium tabular-nums">{v}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${accentCls[accent]}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RecommendationsPanel({
  recommendations,
}: {
  recommendations: NonNullable<DetailResponse['recommendations']>;
}) {
  const { t } = useTranslation('admin');
  const navigate = useNavigate();
  // Group recs by global vs page so the global ones (which need the
  // global thresholds editor) are visually separated from the per-
  // page ones (which the editor on this very page can fix).
  const globalRecs = recommendations.filter((r) => r.scope === 'global');
  const pageRecs = recommendations.filter((r) => r.scope === 'page');

  return (
    // SOLID amber palette mirrors the diagnostic-notes panel below
    // intentionally — both render reliably under browser dark-mode
    // overrides like Dark Reader where translucent purples (the
    // previous design) became unreadable.
    <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-amber-900 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-700" />
            {t('botCloaking.recommendations.detailPanel.title', { count: recommendations.length })}
          </h2>
          <p className="text-xs text-amber-900/80 mt-0.5">
            {t('botCloaking.recommendations.detailPanel.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/admin/cloaking/recommendations')}
          className="text-xs font-semibold text-amber-900 hover:text-amber-700 underline whitespace-nowrap"
        >
          {t('botCloaking.recommendations.detailPanel.viewAll')}
        </button>
      </div>

      {globalRecs.length > 0 && (
        <div className="mb-3">
          <p className="text-[11px] font-bold text-amber-900/70 uppercase tracking-wider mb-2">
            {t('botCloaking.recommendations.detailPanel.globalGroup')}
          </p>
          <ul className="space-y-2">
            {globalRecs.map((rec) => (
              <RecRow key={rec.id} rec={rec} t={t} />
            ))}
          </ul>
        </div>
      )}

      {pageRecs.length > 0 && (
        <div>
          <p className="text-[11px] font-bold text-amber-900/70 uppercase tracking-wider mb-2">
            {t('botCloaking.recommendations.detailPanel.pageGroup')}
          </p>
          <ul className="space-y-2">
            {pageRecs.map((rec) => (
              <RecRow key={rec.id} rec={rec} t={t} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RecRow({
  rec,
  t,
}: {
  rec: NonNullable<DetailResponse['recommendations']>[number];
  t: ReturnType<typeof useTranslation>['t'];
}) {
  // Solid white tile on top of the amber-50 panel — gives the row
  // its own surface so the severity stripe + content read clearly
  // even when the OS/browser is forcing dark mode.
  const stripeCls = (() => {
    switch (rec.severity) {
      case 'critical':
        return 'border-l-4 border-rose-500';
      case 'high':
        return 'border-l-4 border-orange-500';
      case 'medium':
        return 'border-l-4 border-amber-500';
      default:
        return 'border-l-4 border-slate-300';
    }
  })();
  return (
    <li
      className={`flex items-start gap-3 p-3 rounded-md bg-white border border-amber-200 ${stripeCls}`}
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 leading-snug">
          {renderRationale(rec, t)}
        </p>
        <div className="text-xs text-gray-700 mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {rec.key && (
            <code className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-900 font-mono text-[11px]">
              {rec.key}
            </code>
          )}
          {rec.direction !== 'review' && rec.suggestedValue !== null && (
            <span>
              <span className="text-gray-500">
                {t('botCloaking.recommendations.field.now')}:
              </span>{' '}
              <span className="font-medium tabular-nums text-gray-800">
                {formatRecValue(rec.currentValue)}
              </span>{' '}
              <span className="text-gray-400">→</span>{' '}
              <span className="font-bold text-amber-800 tabular-nums">
                {formatRecValue(rec.suggestedValue)}
              </span>
            </span>
          )}
          <span className="text-gray-700">
            {t('botCloaking.recommendations.field.evidence')}:{' '}
            <span className="font-semibold tabular-nums">{rec.evidenceCount}</span>
            {rec.evidencePct > 0 && (
              <span className="text-gray-500 ml-1">
                ({(rec.evidencePct * 100).toFixed(0)}%)
              </span>
            )}
          </span>
        </div>
      </div>
    </li>
  );
}

/**
 * Pull the rendered rationale string out of i18n with a friendly
 * fallback. The previous implementation dumped JSON.stringify of the
 * rationaleVars when the key was missing — visually catastrophic.
 *
 * IMPORTANT: i18n.exists() does NOT honour the hook's namespace
 * context (it always queries the default namespace). Use t() with
 * a `defaultValue` instead so the namespace bound to this useTranslation
 * call is respected and the fallback only fires when the key truly
 * isn't translated.
 *
 * Rationale keys MUST use underscores (e.g. `rec_loosen_knob_fp`),
 * never dots — i18next treats `.` as a key-separator and would walk
 * a dotted id into a non-existent nested path.
 */
function renderRationale(
  rec: NonNullable<DetailResponse['recommendations']>[number],
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const i18nKey = `botCloaking.recommendations.rationale.${rec.rationaleKey}`;
  // Friendly fallback — no JSON. Surfaces the rule id + the most
  // useful var so admins still get something readable when an
  // engine update ships ahead of the translation file.
  const ev = rec.evidenceCount ? ` (${rec.evidenceCount} blocks)` : '';
  const fallback = `${rec.rationaleKey.replace(/_/g, ' ')}${ev}`;
  return t(i18nKey, {
    defaultValue: fallback,
    ...(rec.rationaleVars as Record<string, unknown>),
  });
}

function formatRecValue(v: unknown): string {
  if (typeof v === 'boolean') return v ? 'on' : 'off';
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function RangePicker({ value, onChange }: { value: Range; onChange: (r: Range) => void }) {
  const opts: Range[] = ['today', 'yesterday', '7d', '30d'];
  return (
    <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 text-sm">
      {opts.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
            value === o ? 'bg-orange-500 text-white' : 'text-gray-700 hover:bg-gray-100'
          }`}
        >
          {RANGE_LABELS[o]}
        </button>
      ))}
    </div>
  );
}
